import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api } from "../api.ts";
import { dateTime, money, periodName } from "../format.ts";
import { applyTheme, canEdit, useMe } from "../session.ts";
import {
  Badge,
  Button,
  cx,
  Empty,
  ErrorText,
  Field,
  Input,
  Loading,
  Notice,
  Page,
  Select,
  SettingsSection,
  SettingsTab,
  Table,
  Tabs,
  Td,
  Textarea,
} from "../ui.tsx";
import { PasskeyButton } from "./auth.tsx";
import { ClaudeConnections } from "./claude.tsx";

type Tab = "general" | "team" | "security" | "billing" | "audit";
const ANCHOR_TAB: Record<string, Tab> = {
  organization: "general",
  forwarding: "general",
  "auto-send": "general",
  appearance: "general",
  export: "general",
  notifications: "general",
  alerts: "general",
  delete: "general",
  claude: "general",
  team: "team",
  members: "team",
  invite: "team",
  invitations: "team",
  security: "security",
  "two-factor": "security",
  password: "security",
  passkeys: "security",
  sessions: "security",
  sso: "security",
  "sso-policy": "security",
  scim: "security",
  plan: "billing",
  invoices: "billing",
  plans: "billing",
  usage: "billing",
  audit: "audit",
};

export function Settings() {
  const loc = useLocation();
  const nav = useNavigate();
  const anchor = loc.hash.slice(1);
  const tab: Tab =
    ANCHOR_TAB[anchor] ??
    (["general", "team", "security", "billing", "audit"].includes(anchor)
      ? (anchor as Tab)
      : "general");
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  useEffect(() => {
    if (!anchor || !ANCHOR_TAB[anchor]) return;
    // A card that loads its own data appears later: scroll to it once it's there.
    const go = () => {
      const el = document.getElementById(anchor);
      el?.scrollIntoView();
      return !!el;
    };
    if (go()) return;
    const watch = new MutationObserver(() => go() && watch.disconnect());
    watch.observe(document.body, { childList: true, subtree: true });
    const stop = setTimeout(() => watch.disconnect(), 5000);
    return () => {
      watch.disconnect();
      clearTimeout(stop);
    };
  }, [anchor]);
  return (
    <Page help="settings" title="Settings">
      <Tabs
        label="Settings"
        value={tab}
        onChange={(t) => nav(`/settings#${t}`, { replace: true })}
        tabs={[
          { id: "general", label: "General" },
          { id: "team", label: "Team" },
          { id: "security", label: "Security" },
          { id: "billing", label: "Billing" },
          ...(admin ? [{ id: "audit" as const, label: "Audit log" }] : []),
        ]}
      >
        {tab === "general" && <General />}
        {tab === "team" && <Team />}
        {tab === "security" && <Security />}
        {tab === "billing" && <Billing />}
        {tab === "audit" && admin && <Audit />}
      </Tabs>
    </Page>
  );
}

function General() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const { data, isLoading } = useQuery({
    queryKey: ["org"],
    queryFn: () =>
      api.get<{
        org: { name: string; time_zone: string; currency: string };
        forwarding: { vendorId: string; vendor: string; address: string }[];
        autoSendHoldMinutes: number;
        slackApp?: boolean;
        alertTests?: { email: AlertTest | null; slack: AlertTest | null };
      }>("/org"),
  });
  const [f, setF] = useState({ name: "", timeZone: "", currency: "" });
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("recoup.theme") ?? "system";
    } catch {
      return "system";
    }
  });
  useEffect(() => {
    if (data)
      setF({ name: data.org.name, timeZone: data.org.time_zone, currency: data.org.currency });
  }, [data]);
  const save = useMutation({
    mutationFn: () => api.patch("/org", f),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["org"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  if (isLoading || !data) return <Loading />;
  const dirty =
    f.name !== data.org.name ||
    f.timeZone !== data.org.time_zone ||
    f.currency !== data.org.currency;
  return (
    <SettingsTab>
      <SettingsSection
        id="organization"
        title="Organization"
        description="Your company's name on claim letters, and how recoup shows dates and money."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <div className="grid max-w-2xl gap-4 sm:grid-cols-3">
            <Field label="Name">
              {(id) => (
                <Input
                  id={id}
                  readOnly={!admin}
                  value={f.name}
                  onChange={(e) => setF({ ...f, name: e.target.value })}
                />
              )}
            </Field>
            <Field label="Time zone">
              {(id) => (
                <Input
                  id={id}
                  readOnly={!admin}
                  value={f.timeZone}
                  onChange={(e) => setF({ ...f, timeZone: e.target.value })}
                />
              )}
            </Field>
            <Field label="Currency">
              {(id) => (
                <Input
                  id={id}
                  readOnly={!admin}
                  maxLength={3}
                  value={f.currency}
                  onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })}
                />
              )}
            </Field>
          </div>
          <ErrorText error={save.error} />
          {admin && (
            <div className="flex justify-end gap-2 border-t border-line pt-4">
              <Button
                disabled={!dirty}
                onClick={() =>
                  setF({
                    name: data.org.name,
                    timeZone: data.org.time_zone,
                    currency: data.org.currency,
                  })
                }
              >
                Discard
              </Button>
              <Button type="submit" variant="primary" disabled={!dirty} busy={save.isPending}>
                Save changes
              </Button>
            </div>
          )}
        </form>
      </SettingsSection>
      <SettingsSection
        id="auto-send"
        title="Claims sent for you"
        description="Turn it on per vendor, on the vendor's page."
      >
        <p className="text-sm">
          Before recoup sends a claim itself, it emails your owners and admins and waits{" "}
          {data.autoSendHoldMinutes} minutes, so anyone can hold it.
        </p>
      </SettingsSection>
      <SettingsSection
        id="forwarding"
        title="Vendor forwarding addresses"
        description="Forward or CC vendor support threads and replies; recoup files them on the vendor."
        flush
      >
        {data.forwarding.length === 0 ? (
          <Empty title="No vendors yet." />
        ) : (
          <Table head={["Vendor", "Address"]}>
            {data.forwarding.map((x) => (
              <tr key={x.address}>
                <Td>
                  <Link className="font-medium hover:text-accent" to={`/vendors/${x.vendorId}`}>
                    {x.vendor}
                  </Link>
                </Td>
                <Td>
                  <code className="text-sm break-all">{x.address}</code>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </SettingsSection>
      <SettingsSection
        id="notifications"
        title="Alerts"
        description="Deadline reminders, new claims, and claims recoup sends for you."
        flush
      >
        <Alerts
          admin={admin}
          org={data.org as { slack_configured?: boolean; slack_channel?: string | null }}
          slackApp={!!data.slackApp}
          tests={data.alertTests}
        />
      </SettingsSection>
      <SettingsSection
        id="appearance"
        title="Appearance"
        description="How recoup looks on this device."
      >
        <div className="grid max-w-md grid-cols-3 gap-2">
          {["system", "light", "dark"].map((t) => (
            <Button
              key={t}
              variant={theme === t ? "primary" : "secondary"}
              onClick={() => {
                setTheme(t);
                try {
                  if (t === "system") localStorage.removeItem("recoup.theme");
                  else localStorage.setItem("recoup.theme", t);
                } catch {
                  /* storage unavailable */
                }
                applyTheme();
              }}
            >
              {t[0]!.toUpperCase() + t.slice(1)}
            </Button>
          ))}
        </div>
      </SettingsSection>
      {me?.role === "owner" && (
        <SettingsSection
          id="export"
          title="Export your data"
          description="Everything recoup holds for your organization, in one file."
          actions={
            <Button
              onClick={async () => {
                // Exports need a fresh step-up; the dialog asks for it, then the download starts.
                await api.get("/export/ready");
                window.location.href = "/api/export";
              }}
            >
              Download export
            </Button>
          }
        />
      )}
      <ClaudeConnections />
      {me?.role === "owner" && <DeleteOrganization name={data.org.name} />}
    </SettingsTab>
  );
}

/**
 * Deleting the organization: the owner types its name. Its data goes after a grace period, and
 * until then anyone can see when and the owner can cancel.
 */
function DeleteOrganization({ name }: { name: string }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState("");
  const status = useQuery({
    queryKey: ["org-deletion"],
    queryFn: () => api.get<{ graceDays: number; scheduledFor: string | null }>("/org/deletion"),
  });
  const done = () => {
    setConfirm("");
    qc.invalidateQueries({ queryKey: ["org-deletion"] });
  };
  const schedule = useMutation({
    mutationFn: () => api.post("/org/deletion", { confirmName: confirm }),
    onSuccess: done,
  });
  const cancel = useMutation({ mutationFn: () => api.del("/org/deletion"), onSuccess: done });
  const at = status.data?.scheduledFor;
  return (
    <SettingsSection
      id="delete"
      title="Delete this organization"
      description={`Removes every vendor, contract, measurement and claim, and everyone who belongs to no other organization. It happens ${status.data?.graceDays ?? 7} days after you ask, and you can cancel until then. Export your data first if you need it.`}
    >
      {at ? (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-danger">
            This organization will be deleted on {new Date(at).toLocaleString()}.
          </p>
          <Button busy={cancel.isPending} onClick={() => cancel.mutate()}>
            Cancel deletion
          </Button>
        </div>
      ) : (
        <form
          className="flex max-w-xl flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirm === name) schedule.mutate();
          }}
        >
          <Input
            aria-label="Type the organization's name to confirm"
            placeholder={`Type "${name}" to confirm`}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <Button
            type="submit"
            variant="danger"
            disabled={confirm !== name}
            busy={schedule.isPending}
          >
            Delete organization
          </Button>
        </form>
      )}
      <ErrorText error={schedule.error ?? cancel.error} />
    </SettingsSection>
  );
}

interface AlertTest {
  ok: boolean;
  at: string;
  text: string;
}

/** "Send test" for one alert channel, with the last result in a sentence. */
/** Send a test to one channel; the last result, from the server, shows beside it. */
function TestButton({ channel, last }: { channel: "email" | "slack"; last: AlertTest | null }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => api.post("/org/alerts/test", { channel }),
    onSuccess: () => setTimeout(() => qc.invalidateQueries({ queryKey: ["org"] }), 2000),
  });
  return (
    <div className="flex flex-col items-end gap-1">
      <Button busy={m.isPending} onClick={() => m.mutate()}>
        Send a test
      </Button>
      {m.isSuccess && !last && <span className="text-xs text-muted">Sending…</span>}
      {last && (
        <span className={cx("text-xs", last.ok ? "text-muted" : "text-danger")}>{last.text}</span>
      )}
      <ErrorText error={m.error} />
    </div>
  );
}

function AlertRow({
  name,
  detail,
  status,
  children,
}: {
  name: string;
  detail: ReactNode;
  status: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm font-medium">
          {name} {status}
        </div>
        <p className="mt-0.5 text-sm text-muted">{detail}</p>
      </div>
      {children && <div className="flex shrink-0 flex-wrap items-start gap-2">{children}</div>}
    </div>
  );
}

/**
 * Email always; Slack with Add to Slack (a channel picked on Slack's own page), shown only where
 * the Slack app is set up or an org is already connected.
 */
function Alerts({
  admin,
  org,
  slackApp,
  tests,
}: {
  admin: boolean;
  org: { slack_configured?: boolean; slack_channel?: string | null };
  slackApp: boolean;
  tests?: { email: AlertTest | null; slack: AlertTest | null };
}) {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const connected = !!org.slack_configured;
  const start = useMutation({
    mutationFn: () => api.post<{ url: string }>("/org/slack/start"),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  const save = useMutation({
    mutationFn: (webhookUrl: string | null) => api.put("/org/slack", { webhookUrl }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org"] }),
  });
  const back = params.get("slack_error");
  return (
    <div className="divide-y divide-line">
      {params.get("slack") === "connected" && connected && (
        <div className="px-5 py-3">
          <Notice tone="ok">Slack is connected. recoup posted a first message there.</Notice>
        </div>
      )}
      {back && (
        <div className="px-5 py-3">
          <Notice tone="danger">{back}</Notice>
        </div>
      )}
      <AlertRow
        name="Email"
        status={<Badge tone="ok">On</Badge>}
        detail="Owners and admins, 14, 7, 3 and 1 days before each claim deadline, and for every new or automatic claim."
      >
        {admin && <TestButton channel="email" last={tests?.email ?? null} />}
      </AlertRow>
      {/* Slack only where the Slack app is set up, or for an org already connected to it. */}
      {(slackApp || connected) && (
        <AlertRow
          name="Slack"
          status={connected ? <Badge tone="ok">Connected</Badge> : <Badge>Not connected</Badge>}
          detail={
            connected
              ? `The same alerts, posted to ${org.slack_channel ?? "your Slack channel"}.`
              : "Post the same alerts to your team's Slack channel."
          }
        >
          {admin && connected && (
            <>
              <TestButton channel="slack" last={tests?.slack ?? null} />
              <Button variant="ghost" busy={save.isPending} onClick={() => save.mutate(null)}>
                Disconnect
              </Button>
            </>
          )}
          {admin && !connected && slackApp && (
            <Button variant="primary" busy={start.isPending} onClick={() => start.mutate()}>
              Add to Slack
            </Button>
          )}
        </AlertRow>
      )}
      {(start.error || save.error) && (
        <div className="px-5 py-3">
          <ErrorText error={start.error ?? save.error} />
        </div>
      )}
    </div>
  );
}

/**
 * An admin resets a member's two-factor after checking who they are (the reason records how).
 * The member is signed out and sets it up again at their next sign-in.
 */
function ResetTwoFactor({ userId, name }: { userId: string; name: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const m = useMutation({
    mutationFn: () => api.post(`/members/${userId}/reset-mfa`, { reason }),
    onSuccess: () => {
      setOpen(false);
      setReason("");
      qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
  if (!open)
    return (
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Reset two-factor
      </Button>
    );
  return (
    <form
      className="flex flex-col items-end gap-2 text-left"
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim().length >= 10) m.mutate();
      }}
    >
      <Input
        aria-label={`How you checked it's ${name}`}
        placeholder="How you checked it's them (e.g. video call)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex gap-1">
        <Button
          type="submit"
          variant="danger"
          busy={m.isPending}
          disabled={reason.trim().length < 10}
        >
          Reset
        </Button>
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
      <ErrorText error={m.error} />
    </form>
  );
}

interface MemberRow {
  id: string;
  name: string;
  email: string;
  role: string;
  mfa: boolean;
}

function Team() {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  const { data, isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: () =>
      api.get<{
        members: MemberRow[];
        invitations: { id: string; email: string; role: string; expires_at: string }[];
      }>("/members"),
  });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const m = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members"] }),
  });
  if (isLoading || !data) return <Loading />;
  return (
    <SettingsTab>
      <SettingsSection
        id="members"
        title="Members"
        description="Owners and admins manage the team and approve terms; members prepare and send claims; viewers can look."
        actions={<ErrorText error={m.error} />}
        flush
      >
        <Table head={["Name", "Role", "Two-factor", ""]}>
          {data.members.map((u) => (
            <tr key={u.id}>
              <Td>
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-muted">{u.email}</div>
              </Td>
              <Td>
                {admin && u.id !== me?.user?.id ? (
                  <Select
                    aria-label={`Role for ${u.name}`}
                    className="w-32"
                    value={u.role}
                    onChange={(e) =>
                      m.mutate(() => api.patch(`/members/${u.id}`, { role: e.target.value }))
                    }
                  >
                    {(me?.role === "owner"
                      ? ["owner", "admin", "member", "viewer"]
                      : ["admin", "member", "viewer"]
                    ).map((r) => (
                      <option key={r} value={r}>
                        {r[0]!.toUpperCase() + r.slice(1)}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span className="capitalize">{u.role}</span>
                )}
              </Td>
              <Td>{u.mfa ? <Badge tone="ok">On</Badge> : <Badge tone="warn">Not set up</Badge>}</Td>
              <Td className="text-right">
                {admin && u.id !== me?.user?.id && (
                  <div className="flex justify-end gap-1">
                    {u.mfa && <ResetTwoFactor userId={u.id} name={u.name || u.email} />}
                    <Button
                      variant="ghost"
                      onClick={() => m.mutate(() => api.del(`/members/${u.id}`))}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </SettingsSection>
      {admin && (
        <SettingsSection
          id="invite"
          title="Invite someone"
          description="They get an email with a link that works for 7 days."
        >
          <form
            className="flex max-w-2xl flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              m.mutate(() => api.post("/invitations", { email, role }), {
                onSuccess: () => setEmail(""),
              });
            }}
          >
            <Input
              type="email"
              placeholder="name@company.com"
              aria-label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Select
              aria-label="Role"
              className="sm:w-36"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </Select>
            <Button type="submit" variant="primary" disabled={!email} busy={m.isPending}>
              Send invitation
            </Button>
          </form>
        </SettingsSection>
      )}
      {admin && data.invitations.length > 0 && (
        <SettingsSection
          id="invitations"
          title="Pending invitations"
          description="Invitations not yet accepted."
          flush
        >
          <Table head={["Email", "Role", "Expires", ""]}>
            {data.invitations.map((i) => (
              <tr key={i.id}>
                <Td>{i.email}</Td>
                <Td className="capitalize">{i.role}</Td>
                <Td>{dateTime(i.expires_at)}</Td>
                <Td className="text-right">
                  <Button
                    variant="ghost"
                    onClick={() => m.mutate(() => api.del(`/invitations/${i.id}`))}
                  >
                    Revoke
                  </Button>
                </Td>
              </tr>
            ))}
          </Table>
        </SettingsSection>
      )}
    </SettingsTab>
  );
}

function SecurityBasics() {
  const qc = useQueryClient();
  const [pw, setPw] = useState({ current: "", next: "" });
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: () => api.post("/auth/password", pw),
    onSuccess: () => {
      setDone(true);
      setPw({ current: "", next: "" });
      qc.invalidateQueries({ queryKey: ["sessions"] });
    },
  });
  return (
    <>
      <SettingsSection
        id="two-factor"
        title="Two-factor authentication"
        description="recoup requires it for everyone."
      >
        <Badge tone="ok">On</Badge>
      </SettingsSection>
      <SettingsSection
        id="password"
        title="Password"
        description="Changing it signs you out everywhere else."
      >
        <form
          className="flex max-w-md flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate();
          }}
        >
          <Field label="Current password">
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })}
              />
            )}
          </Field>
          <Field label="New password" hint="At least 12 characters.">
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                minLength={12}
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
              />
            )}
          </Field>
          <ErrorText error={change.error} />
          {done && <Notice tone="ok">Your password is changed.</Notice>}
          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={!pw.current || pw.next.length < 12}
              busy={change.isPending}
            >
              Change password
            </Button>
          </div>
        </form>
      </SettingsSection>
    </>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const sessions = useQuery({
    queryKey: ["sessions"],
    queryFn: () =>
      api.get<{
        sessions: {
          id: string;
          last_seen_at: string;
          user_agent: string | null;
          ip: string | null;
          current: boolean;
        }[];
      }>("/auth/sessions"),
  });
  const revoke = useMutation({
    mutationFn: () => api.post("/auth/sessions/revoke-others"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  return (
    <SettingsSection
      id="sessions"
      title="Sessions"
      description="Where you're signed in."
      flush
      actions={
        <Button onClick={() => revoke.mutate()} busy={revoke.isPending}>
          Sign out other sessions
        </Button>
      }
    >
      <Table head={["Device", "Last active", ""]}>
        {sessions.data?.sessions.map((s) => (
          <tr key={s.id}>
            <Td>
              <div className="max-w-md truncate">{s.user_agent ?? "Unknown device"}</div>
              <div className="text-xs text-muted">{s.ip}</div>
            </Td>
            <Td>{dateTime(s.last_seen_at)}</Td>
            <Td>{s.current && <Badge tone="accent">This device</Badge>}</Td>
          </tr>
        ))}
      </Table>
    </SettingsSection>
  );
}

interface BillingData {
  plan: string;
  plans: Record<
    string,
    {
      name: string;
      price: string;
      summary: string;
      contractLimit: number | null;
      successFeePercent: number;
    }
  >;
  /** recoup charges this company nothing (design partners). */
  feesWaived: boolean;
  /** Stripe is set up on this recoup. */
  payments: boolean;
  cardOnFile: boolean;
  invoices: {
    period: string;
    status: "creating" | "open" | "paid" | "failed" | "void";
    vendor_count: number;
    vendor_fee_cents: string;
    success_fee_cents: string;
    hosted_url: string | null;
  }[];
  contracts: number;
  vendors: number;
  monthlyFeeCents: string;
  usage: { kind: string; n: number }[];
  recoveredThisMonth: { currency: string; cents: string; successFeeCents: string }[];
  contact: string | null;
}

const USAGE: Record<string, string> = {
  contract_extraction: "Contracts read",
  claim_letter: "Claim letters drafted",
  dispute_reply: "Dispute replies drafted",
  reply_classification: "Vendor replies read",
  vendor_setup: "Vendors set up",
};

const INVOICE: Record<string, { label: string; tone: "ok" | "warn" | "danger" | "neutral" }> = {
  creating: { label: "Being prepared", tone: "neutral" },
  open: { label: "Charging", tone: "warn" },
  paid: { label: "Paid", tone: "ok" },
  failed: { label: "Payment failed", tone: "danger" },
  void: { label: "Cancelled", tone: "neutral" },
};

function Billing() {
  const { data: me } = useMe();
  const owner = me?.role === "owner";
  const [params] = useSearchParams();
  const { data, isLoading } = useQuery({
    queryKey: ["billing"],
    queryFn: () => api.get<BillingData>("/billing"),
  });
  // Both open a page on Stripe; recoup never sees the card.
  const go = useMutation({
    mutationFn: (to: "checkout" | "portal") => api.post<{ url: string }>(`/billing/${to}`),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  if (isLoading || !data) return <Loading />;
  const current = data.plans[data.plan]!;
  const paying = data.plan === "recover" && !data.feesWaived;
  const action =
    !data.payments || !owner
      ? null
      : data.cardOnFile
        ? { to: "portal" as const, label: "Manage billing" }
        : data.plan === "free_audit"
          ? { to: "checkout" as const, label: "Choose Recover" }
          : paying
            ? { to: "checkout" as const, label: "Add a card" }
            : null;
  return (
    <SettingsTab>
      <SettingsSection
        id="plan"
        title="Your plan"
        description={
          data.feesWaived
            ? "recoup isn't charging you: every vendor monitored, claims drafted and filed, no fees."
            : current.summary
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-lg font-semibold">{current.name}</span>
          <Badge tone="accent">{data.feesWaived ? "Free for you" : current.price}</Badge>
          {current.contractLimit !== null && (
            <span className="text-sm text-muted">
              {data.contracts} of {current.contractLimit} contracts used
            </span>
          )}
          {current.contractLimit === null && (
            <span className="text-sm text-muted">
              {data.vendors} {data.vendors === 1 ? "vendor" : "vendors"} monitored
              {/* A free plan has no fee, so no minimum to explain. */}
              {data.monthlyFeeCents !== "0" && (
                <>
                  {" "}
                  · <span className="tabular">{money(data.monthlyFeeCents, "USD")}</span> this month
                  {data.vendors < 5 && " (the 5-vendor minimum)"}
                </>
              )}
            </span>
          )}
        </div>
        {params.get("billing") === "card_saved" && (
          <div className="mt-4">
            <Notice tone="ok">
              Card saved with Stripe. You're on Recover; your first bill comes after your first full
              month.
            </Notice>
          </div>
        )}
        {action && (
          <div className="mt-4 flex flex-col gap-2">
            {paying && !data.cardOnFile && (
              <p className="text-sm text-muted">Add a card so recoup can bill you each month.</p>
            )}
            <div>
              <Button
                variant={action.to === "checkout" ? "primary" : "secondary"}
                busy={go.isPending}
                onClick={() => go.mutate(action.to)}
              >
                {action.label}
              </Button>
            </div>
            <ErrorText error={go.error} />
          </div>
        )}
      </SettingsSection>
      {data.invoices.length > 0 && (
        <SettingsSection
          id="invoices"
          title="Bills"
          description="Each month's bill, charged to your card by Stripe."
          flush
        >
          <Table head={["Month", "Vendors", "Success fee", "Total", "Status", ""]}>
            {data.invoices.map((i) => (
              <tr key={i.period}>
                <Td>{periodName(i.period, true)}</Td>
                <Td className="tabular">{money(i.vendor_fee_cents, "USD")}</Td>
                <Td className="tabular">{money(i.success_fee_cents, "USD")}</Td>
                <Td className="tabular font-medium">
                  {money(String(BigInt(i.vendor_fee_cents) + BigInt(i.success_fee_cents)), "USD")}
                </Td>
                <Td>
                  <Badge tone={INVOICE[i.status]?.tone ?? "neutral"}>
                    {INVOICE[i.status]?.label ?? i.status}
                  </Badge>
                </Td>
                <Td>
                  {i.hosted_url && (
                    <a className="text-accent" href={i.hosted_url} target="_blank" rel="noreferrer">
                      Invoice
                    </a>
                  )}
                </Td>
              </tr>
            ))}
          </Table>
        </SettingsSection>
      )}
      <SettingsSection
        id="plans"
        title="Plans"
        description={
          data.payments && data.plan === "free_audit"
            ? "Choose Recover above to add a card and start."
            : data.contact
              ? `To change plans, email ${data.contact} from an owner's address.`
              : "To change plans, contact the recoup team from an owner's address."
        }
      >
        <div className="grid gap-4 md:grid-cols-2">
          {Object.entries(data.plans).map(([id, p]) => (
            <div
              key={id}
              className={`rounded-control border p-4 ${id === data.plan ? "border-accent bg-accent-soft" : "border-line"}`}
            >
              <div className="font-semibold">{p.name}</div>
              <div className="mt-1 text-sm">{p.price}</div>
              <p className="mt-2 text-sm text-muted">{p.summary}</p>
            </div>
          ))}
        </div>
      </SettingsSection>
      <SettingsSection
        id="usage"
        title="This month"
        description="What recoup has done for you since the 1st."
        flush
      >
        <Table
          head={["What recoup did", "Count"]}
          empty={data.usage.length === 0 && <Empty title="Nothing yet this month." />}
        >
          {data.usage.map((u) => (
            <tr key={u.kind}>
              <Td>{USAGE[u.kind] ?? u.kind}</Td>
              <Td className="tabular">{u.n}</Td>
            </tr>
          ))}
        </Table>
        {data.recoveredThisMonth.length > 0 && (
          <div className="border-t border-line px-5 py-4 text-sm">
            {data.recoveredThisMonth.map((r) => (
              <p key={r.currency}>
                Recovered this month:{" "}
                <span className="tabular font-medium">{money(r.cents, r.currency)}</span>
                {current.successFeePercent > 0 && !data.feesWaived && (
                  <>
                    {" "}
                    · success fee {current.successFeePercent}%:{" "}
                    <span className="tabular">{money(r.successFeeCents, r.currency)}</span>
                  </>
                )}
              </p>
            ))}
          </div>
        )}
      </SettingsSection>
    </SettingsTab>
  );
}

function Security() {
  const { data: me } = useMe();
  const admin = canEdit(me, "admin");
  return (
    <SettingsTab>
      <SecurityBasics />
      <Passkeys />
      <Sessions />
      {admin && <SingleSignOn />}
      {admin && <Scim />}
    </SettingsTab>
  );
}

function Passkeys() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["passkeys"],
    queryFn: () =>
      api.get<{
        passkeys: { id: string; name: string; created_at: string; last_used_at: string | null }[];
      }>("/auth/passkeys"),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/auth/passkeys/${encodeURIComponent(id)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passkeys"] }),
  });
  return (
    <SettingsSection
      id="passkeys"
      title="Passkeys"
      description="Sign in with your device's fingerprint, face or PIN instead of a code."
      flush
    >
      <Table
        head={["Passkey", "Added", "Last used", ""]}
        empty={!data?.passkeys.length && <Empty title="No passkeys yet." />}
      >
        {data?.passkeys.map((p) => (
          <tr key={p.id}>
            <Td className="font-medium">{p.name}</Td>
            <Td>{dateTime(p.created_at)}</Td>
            <Td>{dateTime(p.last_used_at)}</Td>
            <Td className="text-right">
              <Button variant="ghost" onClick={() => del.mutate(p.id)}>
                Remove
              </Button>
            </Td>
          </tr>
        ))}
      </Table>
      <div className="flex flex-col gap-2 border-t border-line px-5 py-4">
        <PasskeyButton
          mode="register"
          label="Add a passkey"
          onDone={() => qc.invalidateQueries({ queryKey: ["passkeys"] })}
        />
        <ErrorText error={del.error} />
      </div>
    </SettingsSection>
  );
}

interface SsoData {
  providers: {
    id: string;
    kind: string;
    name: string;
    domains: string[];
    issuer: string | null;
    entry_point: string | null;
    acsUrl?: string;
    metadataUrl?: string;
  }[];
  redirectUri: string;
  spEntityId: string;
  required: boolean;
}

function SingleSignOn() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["sso"],
    queryFn: () => api.get<SsoData>("/sso/providers"),
  });
  const [f, setF] = useState({
    kind: "oidc",
    name: "",
    domains: "",
    issuer: "",
    clientId: "",
    clientSecret: "",
    entryPoint: "",
    idpCert: "",
  });
  const m = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sso"] }),
  });
  if (!data) return <Loading />;
  const add = () =>
    m.mutate(() =>
      api.post("/sso/providers", {
        kind: f.kind,
        name: f.name,
        domains: f.domains.split(/[\s,]+/).filter(Boolean),
        ...(f.kind === "oidc"
          ? { issuer: f.issuer, clientId: f.clientId, clientSecret: f.clientSecret }
          : { entryPoint: f.entryPoint, idpCert: f.idpCert }),
      }),
    );
  return (
    <>
      <SettingsSection
        id="sso"
        title="Single sign-on"
        description="People with your email domains sign in through your identity provider, and join as members the first time."
        flush
      >
        <Table
          head={["Provider", "Domains", "Details for your identity provider", ""]}
          empty={data.providers.length === 0 && <Empty title="No single sign-on yet." />}
        >
          {data.providers.map((p) => (
            <tr key={p.id}>
              <Td>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-muted uppercase">{p.kind}</div>
              </Td>
              <Td>{p.domains.join(", ")}</Td>
              <Td className="text-xs break-all">
                {p.kind === "oidc" ? (
                  <>
                    Redirect URI: <code>{data.redirectUri}</code>
                  </>
                ) : (
                  <>
                    ACS URL: <code>{p.acsUrl}</code>
                    <br />
                    Entity ID: <code>{data.spEntityId}</code>
                    <br />
                    <a className="text-accent" href={p.metadataUrl}>
                      Service provider metadata
                    </a>
                  </>
                )}
              </Td>
              <Td className="text-right">
                <Button
                  variant="ghost"
                  onClick={() => m.mutate(() => api.del(`/sso/providers/${p.id}`))}
                >
                  Remove
                </Button>
              </Td>
            </tr>
          ))}
        </Table>
        <form
          className="flex flex-col gap-4 border-t border-line p-5"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
        >
          <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
            <Field label="Type">
              {(id) => (
                <Select
                  id={id}
                  value={f.kind}
                  onChange={(e) => setF({ ...f, kind: e.target.value })}
                >
                  <option value="oidc">OpenID Connect</option>
                  <option value="saml">SAML</option>
                </Select>
              )}
            </Field>
            <Field label="Name">
              {(id) => (
                <Input
                  id={id}
                  placeholder="Okta"
                  value={f.name}
                  onChange={(e) => setF({ ...f, name: e.target.value })}
                />
              )}
            </Field>
            <Field label="Email domains">
              {(id) => (
                <Input
                  id={id}
                  placeholder="acme.com"
                  value={f.domains}
                  onChange={(e) => setF({ ...f, domains: e.target.value })}
                />
              )}
            </Field>
          </div>
          {f.kind === "oidc" ? (
            <div className="grid max-w-3xl gap-4 sm:grid-cols-3">
              <Field label="Issuer URL">
                {(id) => (
                  <Input
                    id={id}
                    type="url"
                    value={f.issuer}
                    onChange={(e) => setF({ ...f, issuer: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Client ID">
                {(id) => (
                  <Input
                    id={id}
                    value={f.clientId}
                    onChange={(e) => setF({ ...f, clientId: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Client secret">
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    autoComplete="off"
                    value={f.clientSecret}
                    onChange={(e) => setF({ ...f, clientSecret: e.target.value })}
                  />
                )}
              </Field>
            </div>
          ) : (
            <div className="grid max-w-3xl gap-4">
              <Field label="Single sign-on URL">
                {(id) => (
                  <Input
                    id={id}
                    type="url"
                    value={f.entryPoint}
                    onChange={(e) => setF({ ...f, entryPoint: e.target.value })}
                  />
                )}
              </Field>
              <Field label="Signing certificate (PEM)">
                {(id) => (
                  <Textarea
                    id={id}
                    rows={5}
                    className="font-mono text-xs"
                    value={f.idpCert}
                    onChange={(e) => setF({ ...f, idpCert: e.target.value })}
                  />
                )}
              </Field>
            </div>
          )}
          <ErrorText error={m.error} />
          <div>
            <Button
              type="submit"
              variant="primary"
              busy={m.isPending}
              disabled={!f.name || !f.domains}
            >
              Add provider
            </Button>
          </div>
        </form>
      </SettingsSection>
      <SettingsSection
        id="sso-policy"
        title="Single sign-on policy"
        description="When required, members must sign in through your identity provider. Owners keep their password, so you can't be locked out."
      >
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={data.providers.length === 0}
            checked={data.required}
            onChange={(e) =>
              m.mutate(() => api.put("/org/sso-policy", { required: e.target.checked }))
            }
          />
          Require single sign-on for members
        </label>
      </SettingsSection>
    </>
  );
}

function Scim() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["scim"],
    queryFn: () =>
      api.get<{
        tokens: { id: string; created_at: string; last_used_at: string | null }[];
        baseUrl: string;
      }>("/scim/tokens"),
  });
  const [shown, setShown] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => api.post<{ token: string }>("/scim/tokens"),
    onSuccess: (r) => {
      setShown(r.token);
      qc.invalidateQueries({ queryKey: ["scim"] });
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/scim/tokens/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scim"] }),
  });
  return (
    <SettingsSection
      id="scim"
      title="SCIM provisioning"
      description="Let your identity provider add and remove people automatically."
      actions={
        <Button onClick={() => create.mutate()} busy={create.isPending}>
          Create a token
        </Button>
      }
      flush
    >
      <div className="flex flex-col gap-2 px-5 py-4 text-sm">
        <p>
          SCIM base URL: <code className="break-all">{data?.baseUrl}</code>
        </p>
        {shown && (
          <Notice tone="accent">
            Copy this token now; recoup won't show it again:{" "}
            <code className="break-all">{shown}</code>
          </Notice>
        )}
      </div>
      <Table
        head={["Token", "Created", "Last used", ""]}
        empty={!data?.tokens.length && <Empty title="No SCIM tokens." />}
      >
        {data?.tokens.map((x) => (
          <tr key={x.id}>
            <Td className="font-mono text-xs">{x.id.slice(0, 8)}…</Td>
            <Td>{dateTime(x.created_at)}</Td>
            <Td>{dateTime(x.last_used_at)}</Td>
            <Td className="text-right">
              <Button variant="ghost" onClick={() => revoke.mutate(x.id)}>
                Revoke
              </Button>
            </Td>
          </tr>
        ))}
      </Table>
    </SettingsSection>
  );
}

function Audit() {
  const { data, isLoading } = useQuery({
    queryKey: ["audit"],
    queryFn: () =>
      api.get<{
        entries: {
          id: string;
          actor_label: string;
          action: string;
          target: string | null;
          details: Record<string, unknown>;
          at: string;
        }[];
      }>("/audit"),
  });
  if (isLoading) return <Loading />;
  return (
    <SettingsTab>
      <SettingsSection
        id="audit"
        title="Audit log"
        description="Every security- and money-relevant action, append-only."
        flush
      >
        <Table
          head={["When", "Who", "What", "Details"]}
          empty={!data?.entries.length && <Empty title="Nothing recorded yet." />}
        >
          {data?.entries.map((e) => (
            <tr key={e.id}>
              <Td className="whitespace-nowrap">{dateTime(e.at)}</Td>
              <Td>{e.actor_label}</Td>
              <Td>
                <code className="text-xs">{e.action}</code>
              </Td>
              <Td className="max-w-md text-xs break-all text-muted">
                {Object.keys(e.details).length ? JSON.stringify(e.details) : ""}
              </Td>
            </tr>
          ))}
        </Table>
      </SettingsSection>
    </SettingsTab>
  );
}
